import { useState, useEffect, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase, getAdminByUserId, hasPanelAccess } from '../lib/supabase';

/**
 * Guard central de autenticação para rotas administrativas.
 * Só renderiza os filhos se houver um admin logado com acesso ao painel.
 * - Não logado  -> redireciona para /admin (tela de login do painel)
 * - Logado sem acesso admin -> tela de acesso restrito
 */
export default function AdminGuard({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<'loading' | 'ok' | 'denied' | 'anon'>('loading');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          if (alive) setStatus('anon');
          return;
        }
        const admin = await getAdminByUserId(user.id);
        if (alive) setStatus(admin && hasPanelAccess(admin) ? 'ok' : 'denied');
      } catch {
        if (alive) setStatus('denied');
      }
    })();
    return () => { alive = false; };
  }, []);

  if (status === 'loading') {
    return (
      <div className="p-10 text-center text-gray-500">
        <i className="ri-loader-4-line animate-spin text-3xl"></i>
        <div className="mt-2">Verificando acesso...</div>
      </div>
    );
  }

  if (status === 'anon') {
    return <Navigate to="/admin" replace />;
  }

  if (status === 'denied') {
    return (
      <div className="p-6 max-w-md mx-auto text-center mt-16">
        <i className="ri-lock-2-line text-5xl text-red-500"></i>
        <h2 className="text-2xl font-bold text-gray-900 mt-3">Acesso restrito</h2>
        <p className="text-gray-500 mt-1">Você precisa estar logado como administrador para acessar esta página.</p>
        <a
          href="/admin"
          className="inline-block mt-5 px-5 py-2.5 rounded-lg font-semibold text-white"
          style={{ backgroundColor: '#006B2B' }}
        >
          Ir para o login
        </a>
      </div>
    );
  }

  return <>{children}</>;
}
